/**
 * Kiosk mode — the spare-iPad kitchen display. Full-screen, glanceable, LARGE type, high
 * contrast, no controls to speak of: it is a display, not an app. It reads only
 * household-wide, non-money endpoints (NEVER money — the counter is visible to everyone) and
 * auto-refreshes every few minutes.
 *
 * Every tile loads independently and states its own truth. A read that fails shows a quiet
 * "couldn't load" on that one tile — never a fabricated value, and never taking the whole
 * board down with it.
 *
 * Prop-free: the integrator adds a /kiosk route and renders <KioskPage />. It still sits
 * behind Cloudflare Access (the iPad holds a long Access session).
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { CalendarEvent, MealPlanEntry, Message, SpecialDate, Weather } from '../../shared/types';
import { useAsync, WeatherGlyph, type Async } from '../weather/ui';
import { temp } from '../weather/weatherApi';
import {
  getLatestNote,
  getNextEvent,
  getTonightDinner,
  getUpcomingDate,
  getWeather,
} from './kioskApi';

const REFRESH_MS = 5 * 60 * 1000; // pull fresh data every 5 minutes

export default function KioskPage() {
  // One tick drives every data tile's refresh; the clock keeps its own faster beat.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#201c19] text-canvas">
      <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-5 p-6 sm:p-10">
        <Clock />
        <WeatherTile tick={tick} />
        <div className="grid gap-4 sm:grid-cols-2">
          <DinnerTile tick={tick} />
          <EventTile tick={tick} />
          <BoardTile tick={tick} />
          <BirthdayTile tick={tick} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- clock

function Clock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const [hm, ap] = time.split(' ');
  const date = now.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <header>
      <div className="flex items-baseline gap-3">
        <span className="font-display text-7xl leading-none font-semibold tracking-tight sm:text-8xl">
          {hm}
        </span>
        {ap && <span className="text-2xl font-medium text-canvas/55 sm:text-3xl">{ap}</span>}
      </div>
      <p className="mt-2 text-xl text-canvas/70 sm:text-2xl">{date}</p>
    </header>
  );
}

// ---------------------------------------------------------------- tile frame

function Tile({
  title,
  className = '',
  children,
}: {
  title: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`rounded-2xl border border-white/10 bg-white/[0.04] p-6 ${className}`}>
      <h2 className="text-sm font-semibold tracking-[0.14em] text-canvas/45 uppercase">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/** The shared honest-state renderer. Error first, so a failed refresh never passes stale data
 *  off as live; otherwise show data, then a first-load pulse, then the empty message. */
function TileBody<T>({
  a,
  empty,
  children,
}: {
  a: Async<T>;
  empty: string;
  children: (data: NonNullable<T>) => ReactNode;
}) {
  if (a.error !== null) return <Quiet>Couldn’t load right now.</Quiet>;
  if (a.data != null) return <>{children(a.data as NonNullable<T>)}</>;
  if (a.loading)
    return (
      <div className="h-9 w-2/3 animate-pulse rounded-full bg-white/10" role="status">
        <span className="sr-only">Loading…</span>
      </div>
    );
  return <Quiet>{empty}</Quiet>;
}

function Quiet({ children }: { children: ReactNode }) {
  return <p className="text-lg text-canvas/45">{children}</p>;
}

// ---------------------------------------------------------------- weather tile

function WeatherTile({ tick }: { tick: number }) {
  const a = useAsync<Weather>(getWeather, [tick]);
  return (
    <Tile title="Weather">
      <TileBody a={a} empty="No location set yet.">
        {(w) => {
          if (!w.configured) return <Quiet>No location set yet.</Quiet>;
          const today = w.daily[0] ?? null;
          const upcoming = w.daily.slice(1, 6);
          return (
            <div>
              <div className="flex items-center gap-5">
                {w.current && (
                  <WeatherGlyph code={w.current.code} className="h-16 w-16 shrink-0 text-honey" />
                )}
                <div className="min-w-0">
                  <p className="font-display text-6xl leading-none font-semibold">
                    {w.current ? temp(w.current.temp_f) : '—'}
                  </p>
                  <p className="mt-1 text-xl text-canvas/70">
                    {w.current?.label ?? 'Unavailable'}
                    {today && (
                      <span className="whitespace-nowrap">
                        {' '}· H {temp(today.high_f)} / L {temp(today.low_f)}
                      </span>
                    )}
                  </p>
                  {w.place && <p className="text-base text-canvas/45">{w.place}</p>}
                </div>
              </div>
              {upcoming.length > 0 && (
                <ul className="mt-5 flex gap-3 overflow-x-auto border-t border-white/10 pt-4">
                  {upcoming.map((day) => (
                    <li key={day.date} className="flex min-w-16 flex-col items-center gap-1">
                      <span className="text-sm text-canvas/55">{weekday(day.date)}</span>
                      <WeatherGlyph code={day.code} className="h-7 w-7 text-canvas/70" />
                      <span className="sr-only">{day.label}. </span>
                      <span className="text-lg font-semibold">{temp(day.high_f)}</span>
                      <span className="text-sm text-canvas/45">{temp(day.low_f)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        }}
      </TileBody>
    </Tile>
  );
}

// ---------------------------------------------------------------- dinner tile

function DinnerTile({ tick }: { tick: number }) {
  const a = useAsync<MealPlanEntry | null>(getTonightDinner, [tick]);
  return (
    <Tile title="Tonight's dinner">
      <TileBody a={a} empty="Nothing planned tonight.">
        {(entry) => (
          <p className="font-display text-4xl leading-tight font-semibold">
            {entry.meal?.name ?? 'Planned'}
          </p>
        )}
      </TileBody>
    </Tile>
  );
}

// ---------------------------------------------------------------- calendar tile

function EventTile({ tick }: { tick: number }) {
  const a = useAsync<CalendarEvent | null>(getNextEvent, [tick]);
  return (
    <Tile title="Next up">
      <TileBody a={a} empty="Nothing coming up.">
        {(e) => (
          <div>
            <p className="font-display text-3xl leading-tight font-semibold">{e.title}</p>
            <p className="mt-1 text-xl text-honey">{eventWhen(e)}</p>
            {e.location && <p className="mt-1 text-base text-canvas/50">{e.location}</p>}
          </div>
        )}
      </TileBody>
    </Tile>
  );
}

// ---------------------------------------------------------------- board tile

function BoardTile({ tick }: { tick: number }) {
  const a = useAsync<Message | null>(getLatestNote, [tick]);
  return (
    <Tile title="On the board">
      <TileBody a={a} empty="The board is clear.">
        {(m) => (
          <div>
            <p className="text-2xl leading-snug">{truncate(m.body, 140)}</p>
            <p className="mt-2 flex items-center gap-2 text-base text-canvas/55">
              {m.author && (
                <span
                  aria-hidden="true"
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: m.author.color }}
                />
              )}
              {m.author?.name ?? 'Someone'} · {ago(m.created_at)}
            </p>
          </div>
        )}
      </TileBody>
    </Tile>
  );
}

// ---------------------------------------------------------------- birthday tile

function BirthdayTile({ tick }: { tick: number }) {
  const a = useAsync<SpecialDate | null>(getUpcomingDate, [tick]);
  return (
    <Tile title="Coming up">
      <TileBody a={a} empty="No birthdays this week.">
        {(d) => (
          <div className="flex items-center gap-4">
            <span aria-hidden="true" className="text-4xl">
              {kindEmoji(d.kind)}
            </span>
            <div>
              <p className="font-display text-3xl leading-tight font-semibold">{d.name}</p>
              <p className="mt-1 text-xl text-honey">
                {whenLabel(d.days_away)}
                {d.turning != null && d.kind === 'birthday' && (
                  <span className="text-canvas/60"> · turning {d.turning}</span>
                )}
              </p>
            </div>
          </div>
        )}
      </TileBody>
    </Tile>
  );
}

// ---------------------------------------------------------------- formatting

function weekday(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' });
}

function localIso(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function eventWhen(e: CalendarEvent, now = new Date()): string {
  const start = new Date(e.starts_at);
  const today = localIso(now);
  const onToday = e.starts_at.slice(0, 10) === today || localIso(start) === today;
  const dayLabel = onToday
    ? 'Today'
    : start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (e.all_day) return `${dayLabel} · All day`;
  const t = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${dayLabel} · ${t}`;
}

function whenLabel(daysAway: number): string {
  if (daysAway <= 0) return 'Today!';
  if (daysAway === 1) return 'Tomorrow';
  return `In ${daysAway} days`;
}

function kindEmoji(kind: SpecialDate['kind']): string {
  if (kind === 'birthday') return '🎂';
  if (kind === 'anniversary') return '💛';
  return '⭐';
}

function truncate(s: string, max: number): string {
  const t = s.replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function ago(iso: string, now = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const m = Math.round((now.getTime() - then) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}
